import {useCallback, useEffect, useState} from "react";
import {MONTHLY} from "../../../util/BookingUtil";
import {getLocationHasRequestedCapacityById} from "../../../components/location/requests/location-requests";
import {getErrorMessageForStandardResponse} from "../../../util/NetworkErrorUtil";
import {DateFormats} from "../../../components/constants/securspace-constants";
import {useFeatureFlags} from "../../routing/useFeatureFlags";
import moment from "moment";

interface CapacityCheckResult {
  hasRequestedCapacity: boolean;
  /** First requested day (yyyy-MM-dd) without room for a non-brokered request; null when the request fits. */
  firstUnavailableDate: string | null;
}

export interface LocationCapacityCheck extends CapacityCheckResult {
  /** Runs the check again after the API rejects a booking because capacity changed. */
  recheck: () => void;
  /**
   * True from a recheck until a fresh check confirms the same request fits. Submit stays blocked meanwhile, and
   * also if that check fails; changing the request lifts it.
   */
  capacityRecheckPending: boolean;
}

const NO_CAPACITY_INFO: CapacityCheckResult = {hasRequestedCapacity: false, firstUnavailableDate: null};

const useLocationHasRequestedCapacity: (
  locationId: string,
  numberOfSpaces: number,
  startDate: string,
  endDate: string,
  frequency: string,
) => LocationCapacityCheck = (locationId, numberOfSpaces, startDate, endDate, frequency) => {
  const {supplierCapacitySchedule} = useFeatureFlags();
  const [capacityCheck, setCapacityCheck] = useState<CapacityCheckResult>(NO_CAPACITY_INFO);
  const [recheckCount, setRecheckCount] = useState(0);
  const requestKey = [locationId, numberOfSpaces, startDate, endDate, frequency].join('|');
  // The request the API rejected for capacity; it can't be resubmitted until a check says it now fits.
  const [rejectedRequestKey, setRejectedRequestKey] = useState<string | null>(null);
  const recheck = useCallback(() => {
    setRejectedRequestKey(requestKey);
    setRecheckCount((count) => count + 1);
  }, [requestKey]);

  useEffect(() => {
    const datesAreValid = moment(startDate, DateFormats.DAY).isValid() && moment(endDate, DateFormats.DAY).isValid();
    // Monthly requests never auto-approve, so they only need the call to check scheduled capacity.
    if (!locationId || !datesAreValid || (frequency === MONTHLY && !supplierCapacitySchedule)) {
      setCapacityCheck(NO_CAPACITY_INFO);
      return;
    }
    let ignore = false;
    const checkedRequestKey = requestKey;
    // Don't keep blocking on a result for different dates while this check runs or if it fails.
    setCapacityCheck((previous) => ({...previous, firstUnavailableDate: null}));
    getLocationHasRequestedCapacityById(locationId, numberOfSpaces, startDate, endDate, frequency)
      .then((response) => {
        if (!ignore) {
          const {hasRequestedCapacity, hasAvailableCapacity, firstUnavailableDate} = response.body;
          if (hasAvailableCapacity === true) {
            // Only a check of the rejected request itself, confirming room, can clear it.
            setRejectedRequestKey((rejected) => (rejected === checkedRequestKey ? null : rejected));
          }
          setCapacityCheck({
            hasRequestedCapacity: frequency !== MONTHLY && !!hasRequestedCapacity,
            firstUnavailableDate: supplierCapacitySchedule && hasAvailableCapacity === false ? firstUnavailableDate ?? null : null,
          });
        }
      })
      .catch((error) => {
        console.error(getErrorMessageForStandardResponse(error, 'Failed to check location capacity'));
      });
    return () => {
      ignore = true;
    };
  }, [locationId, startDate, endDate, numberOfSpaces, frequency, supplierCapacitySchedule, recheckCount, requestKey]);

  return {...capacityCheck, recheck, capacityRecheckPending: rejectedRequestKey === requestKey};
};

export default useLocationHasRequestedCapacity;
